import type { ReactNode } from 'react';
import { cn } from '@/utils/cn';

export type HrSectionTab = {
  id: string;
  label: string;
  hint?: string;
  badge?: number;
};

interface HrSectionTabsProps {
  tabs: HrSectionTab[];
  active: string;
  onChange: (id: string) => void;
  idPrefix: string;
  className?: string;
}

/** Accessible horizontal tabs for HR profile and portal pages. */
export function HrSectionTabs({ tabs, active, onChange, idPrefix, className }: HrSectionTabsProps) {
  return (
    <div
      role="tablist"
      aria-label="Profile sections"
      className={cn(
        'flex gap-1 overflow-x-auto border-b border-slate-200 pb-px dark:border-slate-700',
        className,
      )}
    >
      {tabs.map((tab) => {
        const selected = tab.id === active;
        return (
          <button
            key={tab.id}
            type="button"
            role="tab"
            id={`${idPrefix}-tab-${tab.id}`}
            aria-selected={selected}
            aria-controls={`${idPrefix}-panel-${tab.id}`}
            tabIndex={selected ? 0 : -1}
            onClick={() => onChange(tab.id)}
            className={cn(
              'relative shrink-0 rounded-t-lg px-4 py-2.5 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/40',
              selected
                ? 'border border-b-0 border-slate-200 bg-white text-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100'
                : 'text-slate-500 hover:bg-slate-50 hover:text-slate-800 dark:hover:bg-slate-800/50 dark:hover:text-slate-200',
            )}
          >
            <span className="flex items-center gap-2">
              {tab.label}
              {tab.badge != null && tab.badge > 0 && (
                <span
                  className={cn(
                    'inline-flex min-w-[1.25rem] items-center justify-center rounded-full px-1.5 py-0.5 text-[10px] font-semibold leading-none',
                    selected
                      ? 'bg-primary-100 text-primary-800 dark:bg-primary-950/60 dark:text-primary-300'
                      : 'bg-slate-200 text-slate-600 dark:bg-slate-700 dark:text-slate-300',
                  )}
                  aria-label={`${tab.badge} items`}
                >
                  {tab.badge}
                </span>
              )}
            </span>
          </button>
        );
      })}
    </div>
  );
}

interface HrSectionTabPanelProps {
  tabId: string;
  idPrefix: string;
  active: boolean;
  children: ReactNode;
  className?: string;
}

export function HrSectionTabPanel({
  tabId,
  idPrefix,
  active,
  children,
  className,
}: HrSectionTabPanelProps) {
  if (!active) return null;

  return (
    <div
      role="tabpanel"
      id={`${idPrefix}-panel-${tabId}`}
      aria-labelledby={`${idPrefix}-tab-${tabId}`}
      tabIndex={0}
      className={cn('focus:outline-none', className)}
    >
      {children}
    </div>
  );
}
